import { keyframes } from "@emotion/react";
import { MessageContext } from "@/contexts/MessageContext";
import { memo, useContext } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";

import { AskUploadButton } from "./components/AskUploadButton";
import { MessageAvatar } from "./components/Avatar";
import { MessageActions } from "./components/MessageActions";
import { MessageButtons } from "./components/MessageButtons";
import { MessageContent } from "./components/MessageContent";

import { useLayoutMaxWidth } from "@/hooks/useLayoutMaxWidth";

import { Messages } from "./Messages";
import Step from "./Step";
import UserMessage from "./UserMessage";
import { IAction, IMessageElement, type IStep } from "@/client/types";
import { useConfig } from "@/client/useConfig";
import { CircularProgress, Typography } from "@mui/material";

import ChartDisplay from "@/components/organisms/chat/chartDisplay";

interface Props {
  message: IStep;
  elements: IMessageElement[];
  actions: IAction[];
  indent: number;
  isRunning?: boolean;
  isScorable?: boolean;
  scorableRun?: IStep;
}

const Message = memo(
  ({
    message,
    elements,
    actions,
    isRunning,
    indent,
    isScorable,
    scorableRun,
  }: Props) => {
    const {
      highlightedMessage,
      defaultCollapseContent,
      allowHtml,
      latex,
      onError,
    } = useContext(MessageContext);
    const { config } = useConfig();
    const layoutMaxWidth = useLayoutMaxWidth();
    const isAsk = message.waitForAnswer;
    const isUserMessage = message.role === "user";
    const isStep = message.role !== "user" && message.role !== "assistant";
    // Only keep tool calls if Chain of Thought is tool_call
    const toolCallSkip =
      isStep && config?.ui.cot === "tool_call" && message.role !== "tool";

    const hiddenSkip = isStep && config?.ui.cot === "hidden";

    const skip = toolCallSkip || hiddenSkip;

    if (skip) {
      if (!message.steps) {
        return null;
      }
      return (
        <Messages
          messages={message.steps}
          elements={elements}
          actions={actions}
          indent={indent}
          isRunning={isRunning}
          scorableRun={scorableRun}
        />
      );
    }

    return (
      <>
        <Box
          sx={{
            color: "text.primary",
            position: "relative",
          }}
          className="step"
        >
          <Box
            sx={{
              boxSizing: "border-box",
              mx: "auto",
              width: "100%",
              maxWidth: indent ? "100%" : layoutMaxWidth,
              px: 2,
              display: "flex",
              flexDirection: "column",
              position: "relative",
            }}
          >
            <Stack
              id={`step-${message.id}`}
              direction="row"
              sx={{
                pb: indent ? 1 : 2,
                flexGrow: 1,
                animation:
                  message.id && highlightedMessage === message.id
                    ? `3s ease-in-out 0.1s ${flash}`
                    : "none",
              }}
            >
              {/* User message is displayed differently */}
              {isUserMessage ? (
                <Box display="flex" flexDirection="column" flexGrow={1}>
                  <Box
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="center"
                    gap={1}
                  >
                    <Typography component="p" fontSize="14px" color="#94A3B8">
                      {message.timestamp &&
                        new Date(message.timestamp).toLocaleTimeString(
                          undefined,
                          { hour: "2-digit", minute: "2-digit" }
                        )}
                    </Typography>
                    <Typography fontSize="16px" fontWeight={600} component="h5">
                      {message.name ?? "QNB AI"}
                    </Typography>
                    <MessageAvatar author="You" />
                  </Box>
                  <UserMessage message={message}>
                    <MessageContent
                      elements={elements}
                      message={message}
                      preserveSize={
                        !!message.streaming || !defaultCollapseContent
                      }
                      allowHtml={allowHtml}
                      latex={latex}
                    />
                  </UserMessage>

                  <Box display="flex" justifyContent="flex-end" mt={0.5}>
                    <MessageButtons message={message} />
                  </Box>
                </Box>
              ) : (
                <Stack
                  direction="row"
                  gap="1rem"
                  width="100%"
                  className="ai-message"
                >
                  {message.role === "assistant" && (!isStep || !indent) ? (
                    <MessageAvatar author="Assistant" />
                  ) : null}
                  {/* Display the step and its children */}
                  <Stack direction="column" justifyContent="center">
                    <Stack direction="row" gap={1} alignItems="center">
                      <Typography
                        fontSize="16px"
                        fontWeight={600}
                        component="h5"
                      >
                        {message.name ?? "QNB AI"}
                      </Typography>
                      <Typography component="p" fontSize="14px" color="#94A3B8">
                        {message.timestamp &&
                          new Date(message.timestamp).toLocaleTimeString(
                            undefined,
                            { hour: "2-digit", minute: "2-digit" }
                          )}
                      </Typography>
                    </Stack>

                    <Box
                      sx={{
                        ...(isUserMessage
                          ? {
                              px: 2.5,
                              borderRadius: "1.5rem",
                              backgroundColor: "background.paper",
                              width: "auto",
                              flexGrow: 0,
                              mr: "auto",
                              mt: "6px",
                            }
                          : {
                              px: 0,
                              backgroundColor: "transparent",
                              width: "100%",
                              mt: "6px",
                            }),
                        position: "relative",
                      }}
                    >
                      {isStep ? (
                        <Step step={message} isRunning={isRunning}>
                          {message.steps ? (
                            <Messages
                              messages={message.steps.filter(
                                (s) =>
                                  s.role === "user" || s.role === "assistant"
                              )}
                              elements={elements}
                              actions={actions}
                              indent={indent + 1}
                              isRunning={isRunning}
                            />
                          ) : null}
                          <MessageContent
                            elements={elements}
                            message={message}
                            preserveSize={
                              !!message.streaming || !defaultCollapseContent
                            }
                            allowHtml={allowHtml}
                            latex={latex}
                          />

                          {actions?.length ? (
                            <MessageActions
                              message={message}
                              actions={actions}
                            />
                          ) : null}
                          <MessageButtons message={message} />
                        </Step>
                      ) : (
                        // Display an assistant message
                        <Stack
                          alignItems="flex-start"
                          minWidth={150}
                          flexGrow={1}
                          position="relative"
                        >
                          <ChartDisplay message={message} />
                          <MessageContent
                            elements={elements}
                            message={message}
                            preserveSize={
                              !!message.streaming || !defaultCollapseContent
                            }
                            allowHtml={allowHtml}
                            latex={latex}
                          />

                          {!isRunning && isAsk && (
                            <Box mt={"-16px"} mb={"16px"}>
                              <AskUploadButton onError={onError} />
                            </Box>
                          )}
                          {actions?.length ? (
                            <MessageActions
                              message={message}
                              actions={actions}
                            />
                          ) : null}
                          <MessageButtons message={message} />
                          {scorableRun && isScorable ? (
                            <MessageButtons
                              message={message}
                              run={scorableRun}
                            />
                          ) : null}
                        </Stack>
                      )}
                    </Box>
                  </Stack>
                </Stack>
              )}
            </Stack>
          </Box>
        </Box>
        {/* Make sure the child assistant messages of a step are displayed at the root level. */}
        {message.steps && isStep ? (
          <Messages
            messages={message.steps.filter(
              (s) => s.role === "user" || s.role === "assistant"
            )}
            elements={elements}
            actions={actions}
            indent={0}
            isRunning={isRunning}
            scorableRun={scorableRun}
          />
        ) : null}
        {/* Display the child steps if the message is not a step (usually a user message). */}
        {message.steps && !isStep ? (
          <Messages
            messages={message.steps}
            elements={elements}
            actions={actions}
            indent={indent}
            isRunning={isRunning}
          />
        ) : null}
      </>
    );
  }
);

const Thinking: React.FC<{
  indent: number;
  isLoading: boolean | undefined;
}> = ({ indent, isLoading }) => {
  const layoutMaxWidth = useLayoutMaxWidth();

  if (!isLoading) return null;

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        mx: "auto",
        width: "100%",
        maxWidth: indent ? "100%" : layoutMaxWidth,
        px: 2,
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      <Stack
        id={`step-loading`}
        direction="row"
        sx={{ pb: indent ? 1 : 2, flexGrow: 1 }}
      >
        <Stack direction="row" gap="1rem" width="100%" className="ai-loading">
          {!indent ? <MessageAvatar author={"AI Thinking"} /> : null}

          <Stack direction="column" justifyContent="center">
            <Box
              sx={{
                px: 2.5,
                position: "relative",
                borderRadius: "1.5rem",
                backgroundColor: "background.paper",
                width: "auto",
                flexGrow: 0,
                mr: "auto",
                py: "12px",
              }}
            >
              <CircularProgress size={24} />
            </Box>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
};

// Uses yellow[500] with 50% opacity
const flash = keyframes`
  from {
    background-color: transparent;
  }
  25% {
    background-color: rgba(255, 173, 51, 0.5);
  }
  to {
    background-color: transparent;
  }
`;

export { Message, Thinking };
